import { skuGrossMarginPct } from '../ceoCockpitModel'
import { formatCurrencyExact, formatCount, formatDays } from '../ceoFormat'
import type { CeoCockpitResponse, CeoInventorySku } from '../ceoCockpitTypes'

const NOT_PREPARED = 'Not prepared'

/** "62% GM" — gross margin (margin ÷ price), never markup (margin ÷ cost). */
function skuMarginLabel(sku: CeoInventorySku): string | null {
  const gm = skuGrossMarginPct(sku)
  if (gm == null) return null
  return `${Math.round(gm * 100)}% GM`
}

export function CeoInventoryPanel({ payload, onAsk: _onAsk }: { payload: CeoCockpitResponse; onAsk?: (prompt: string) => void }) {
  const inventory = payload.inventory
  if (!inventory) return null
  const topByRevenue = Array.isArray(inventory.top_by_revenue) ? inventory.top_by_revenue : []
  const stockoutRisk = Array.isArray(inventory.stockout_risk) ? inventory.stockout_risk : []
  const overstock = Array.isArray(inventory.overstock) ? inventory.overstock : []
  return (
    <section className="ceo-section rise" id="ceo-inventory" data-testid="ceo-inventory">
      <p className="ceo-kicker">Inventory</p>
      <div className="ceo-saas-tiles">
        <article className="ceo-mini-tile"><span className="ceo-kicker">On hand</span><b>{formatCount(inventory.on_hand_units)}</b></article>
        <article className="ceo-mini-tile"><span className="ceo-kicker">Value</span><b>{formatCurrencyExact(inventory.on_hand_value)}</b></article>
        <article className={`ceo-mini-tile${inventory.days_of_supply == null ? ' is-empty' : ''}`}><span className="ceo-kicker">Days of supply</span><b>{formatDays(inventory.days_of_supply)}</b></article>
        <article className={`ceo-mini-tile${inventory.turns == null ? ' is-empty' : ''}`}><span className="ceo-kicker">Turns</span><b>{inventory.turns == null || !Number.isFinite(inventory.turns) ? NOT_PREPARED : `${inventory.turns.toFixed(1)}×`}</b></article>
        <article className="ceo-mini-tile"><span className="ceo-kicker">Stockouts</span><b>{formatCount(inventory.stockout_count)}</b></article>
      </div>
      <div className="ceo-inv-cols">
        <div>
          <p className="ceo-kicker">Leaderboard · 30 days</p>
          {topByRevenue.length ? (
            <ul className="ceo-plain-list">
              {topByRevenue.map((sku) => {
                const gm = skuMarginLabel(sku)
                return (
                  <li key={sku.item_id}>
                    <b>
                      {sku.item_name}
                      {sku.sku ? <small className="ceo-muted"> · {sku.sku}</small> : null}
                    </b>
                    <span>
                      {formatCurrencyExact(sku.revenue_30d)}
                      {gm ? ` · ${gm}` : ''}
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="ceo-muted">Not prepared — no item-level sales in the last 30 days.</p>
          )}
        </div>
        <div>
          <p className="ceo-kicker">Stockout risk</p>
          {stockoutRisk.length ? (
            <ul className="ceo-plain-list">
              {stockoutRisk.map((sku) => (
                <li key={sku.item_id}>
                  <b>{sku.item_name}</b>
                  <span>
                    {formatDays(sku.stockout_days_30d)} out · {formatCount(sku.qty_on_hand)} on hand
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ceo-muted">Nothing is at risk of stocking out.</p>
          )}
          <p className="ceo-kicker">Overstock</p>
          {overstock.length ? (
            <ul className="ceo-plain-list">
              {overstock.map((sku) => (
                <li key={sku.item_id}>
                  <b>{sku.item_name}</b>
                  <span>
                    {formatCurrencyExact(sku.on_hand_value)}
                    {sku.days_of_supply != null && Number.isFinite(sku.days_of_supply) ? ` · ${formatDays(sku.days_of_supply)} supply` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ceo-muted">Nothing is overstocked.</p>
          )}
        </div>
      </div>
    </section>
  )
}
